/**
 * Theme Factory
 *
 * Creates MUI themes with dynamic primary colors based on tenant configuration.
 * Accepts an optional primaryColor from the account API to enable multi-tenant theming.
 */

import { createTheme, darken, decomposeColor, lighten, Theme } from '@mui/material/styles';
import breakpoints from './breakpoints';
import colors from './colors';
import components from './components';

/**
 * Creates an app theme with optional custom primary color
 * @param primaryColor Optional hex color from tenant/account API (e.g., '#3ea6d6', 'rgb(62, 166, 214)')
 * @returns MUI Theme object
 */
export const createAppTheme = (primaryColor?: string): Theme => {
  // Use provided color or fall back to default
  const mainColor = primaryColor || colors.primary.main;

  // Expose brand color as CSS variables so tints/gradients can be authored
  // in any context (sx, plain strings, child props) and still track the
  // tenant's primary color. Applied via MuiCssBaseline below:
  //   --brand-rgb      -> "r, g, b" for `rgba(var(--brand-rgb), <alpha>)`
  //                       (and `rgb(var(--brand-rgb))` for solid accents)
  //   --brand-gradient -> the horizontal light->main->dark brand gradient
  const rgbTriplet = (c: string): string => {
    const [r, g, b] = decomposeColor(c).values;
    return `${r}, ${g}, ${b}`;
  };
  const brandVars = (base: string) => ({
    brandRgb: rgbTriplet(base),
    brandGradient: `linear-gradient(90deg, rgb(${rgbTriplet(lighten(base, 0.2))}) 0%, rgb(${rgbTriplet(base)}) 50%, rgb(${rgbTriplet(darken(base, 0.2))}) 100%)`,
  });
  const { brandRgb, brandGradient } = (() => {
    try {
      // Throws if mainColor isn't a parseable rgb/hex (e.g. a named color
      // from a misconfigured tenant); fall back to the default brand.
      return brandVars(mainColor);
    } catch {
      return brandVars(colors.primary.main);
    }
  })();

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: mainColor,
        // MUI automatically generates light/dark from main color. Pin
        // contrastText to white: MUI's derived value resolves to *dark* text
        // for mid-tone brand blues (incl. the default rgb(62,166,214)), which
        // reads poorly on primary-filled surfaces and the gradient CTA. The
        // fill still tracks the tenant's brand color, so branding is respected.
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
        contrastText: colors.secondary.contrastText,
      },
      background: colors.background,
      text: colors.text,
      divider: colors.divider,
      action: colors.action,
      success: colors.success,
      error: colors.error,
      warning: colors.warning,
      info: colors.info,
    },

    breakpoints,

    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      // Heading scale — heavier weight (700) and slightly tighter
      // tracking on the large sizes to match the bold-dark dashboard
      // look (the "Courses" header in the reference design).
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: '-0.015em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: '-0.015em',
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 700,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.5,
        letterSpacing: '-0.005em',
      },
      // Subtitles — used for the grey description line under a page
      // header. Slightly muted weight so the title stays dominant.
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.43,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      // SCOPE-style field labels — small uppercase letter-spaced text
      // sitting above an input or beside a value. Use
      //   <Typography variant="overline">…</Typography>
      // anywhere a labeled section needs that dashboard treatment.
      overline: {
        fontSize: '0.7rem',
        fontWeight: 700,
        lineHeight: 1.5,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
      button: {
        fontSize: '0.9375rem',
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: 0,
      },
    },

    spacing: 8, // Base spacing unit (1 = 8px, 2 = 16px, etc.)

    shape: {
      borderRadius: 8, // Default border radius
    },

    components: {
      ...components,
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--brand-rgb': brandRgb,
            '--brand-gradient': brandGradient,
          },
        },
      },
    },

    // Custom additions for shadows, etc.
    custom: {
      shadow: colors.shadow,
    },
  });
};

// Default theme export (for backward compatibility)
export default createAppTheme();
